import { Fragment, useState } from "react";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { PeriodRow } from "../types";
import {
  getBankingContractBlocks,
  getBankingContractReturnBlocks,
} from "../../../../Rest_api/RestApi";
import { BankingContractAnalyticsBucket } from "../../../../models/banking";

function extractBlockTime(raw: string): string {
  if (!raw) return "";
  const idx = raw.indexOf("T");
  if (idx !== -1) return raw.slice(idx + 1, idx + 6);
  const spaceIdx = raw.indexOf(" ");
  if (spaceIdx !== -1) return raw.slice(spaceIdx + 1, spaceIdx + 6);
  return raw;
}

export function PeriodBreakdownTable({
  rows,
  contractId,
  enableExpand = true,
  mode = "supply",
  title = "Period breakdown",
}: {
  rows: PeriodRow[];
  contractId: string;
  enableExpand?: boolean;
  mode?: "supply" | "return";
  title?: string;
}) {
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const [blockCache, setBlockCache] = useState<
    Record<string, BankingContractAnalyticsBucket[]>
  >({});
  const [loadingDate, setLoadingDate] = useState<string | null>(null);
  const [errorDate, setErrorDate] = useState<{
    date: string;
    message: string;
  } | null>(null);

  const toggleExpand = async (date: string) => {
    if (expandedDate === date) {
      setExpandedDate(null);
      return;
    }
    setExpandedDate(date);
    setErrorDate(null);
    if (blockCache[date]) return;
    if (!contractId) return;
    setLoadingDate(date);
    try {
      const fetcher =
        mode === "return"
          ? getBankingContractReturnBlocks
          : getBankingContractBlocks;
      const data = await fetcher(contractId, date);
      setBlockCache((prev) => ({ ...prev, [date]: data }));
    } catch (err) {
      setErrorDate({
        date,
        message: (err as Error)?.message ?? "Failed to load blocks",
      });
    } finally {
      setLoadingDate((curr) => (curr === date ? null : curr));
    }
  };

  return (
    <div className="bg-white border border-[#ECEEF3] rounded-[10px] px-4 py-3.5 mb-3.5">
      <div className="flex flex-col gap-0.5 mb-2.5">
        <div className="text-[13px] font-semibold text-[#525062]">
          {title}
        </div>
        {enableExpand && (
          <div className="text-[11px] text-[#8C8A96]">
            Click a day to drill into 15-minute blocks
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr>
              <Th align="left">DATE</Th>
              <Th align="right">SCHEDULED MWH</Th>
              <Th align="right">ACTUAL MWH</Th>
              <Th align="right">Δ MWH</Th>
              <Th align="right">Δ %</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isOpen = enableExpand && expandedDate === r.date;
              const isLoading = enableExpand && loadingDate === r.date;
              const blocks = blockCache[r.date] ?? [];
              return (
                <Fragment key={r.date}>
                  <tr
                    onClick={
                      enableExpand ? () => toggleExpand(r.date) : undefined
                    }
                    className={
                      enableExpand ? "cursor-pointer hover:bg-[#FAFBFD]" : ""
                    }
                  >
                    <td className="text-left text-[#1A1B2B] px-3.5 py-2.5 border-b border-[#F4F5F8] tabular-nums">
                      <span className="inline-flex items-center gap-1">
                        {enableExpand &&
                          (isLoading ? (
                            <Loader2
                              size={12}
                              className="text-[#828A99] animate-spin"
                            />
                          ) : isOpen ? (
                            <ChevronDown
                              size={12}
                              className="text-[#828A99]"
                            />
                          ) : (
                            <ChevronRight
                              size={12}
                              className="text-[#C8C6CF]"
                            />
                          ))}
                        {r.date}
                      </span>
                    </td>
                    <td className="text-right text-[#1A1B2B] px-3.5 py-2.5 border-b border-[#F4F5F8] tabular-nums">
                      {r.scheduled.toFixed(2)}
                    </td>
                    <td className="text-right text-[#1A1B2B] px-3.5 py-2.5 border-b border-[#F4F5F8] tabular-nums">
                      {r.actual.toFixed(2)}
                    </td>
                    <td
                      className={`text-right px-3.5 py-2.5 border-b border-[#F4F5F8] tabular-nums ${
                        r.delta < 0 ? "text-[#E0463C]" : "text-[#1F8A39]"
                      }`}
                    >
                      {r.delta.toFixed(2)}
                    </td>
                    <td
                      className={`text-right px-3.5 py-2.5 border-b border-[#F4F5F8] tabular-nums ${
                        r.pct < 0 ? "text-[#E0463C]" : "text-[#1F8A39]"
                      }`}
                    >
                      {r.pct.toFixed(1)}%
                    </td>
                  </tr>

                  {isOpen && errorDate?.date === r.date && (
                    <tr className="bg-[#FFF5F5]">
                      <td
                        colSpan={5}
                        className="px-3.5 py-2 text-[11px] text-[#E0463C] border-b border-[#F4F5F8]"
                      >
                        {errorDate.message}
                      </td>
                    </tr>
                  )}

                  {isOpen &&
                    !isLoading &&
                    !errorDate &&
                    blocks.length === 0 && (
                      <tr className="bg-[#FAFBFD]">
                        <td
                          colSpan={5}
                          className="px-3.5 py-2 text-[11px] text-[#828A99] border-b border-[#F4F5F8]"
                        >
                          No 15-minute block data for this date.
                        </td>
                      </tr>
                    )}

                  {isOpen &&
                    blocks.map((b, idx) => {
                      const timeLabel =
                        b.time ?? extractBlockTime(b.date) ?? b.date;
                      const blockNumber = b.time_block ?? idx + 1;
                      return (
                      <tr
                        key={`${r.date}-${b.time_block ?? idx}`}
                        className="bg-[#FAFBFD]"
                      >
                        <td className="text-left text-[#525062] pl-9 pr-3.5 py-2 border-b border-[#F4F5F8] tabular-nums text-[11px]">
                          <span className="inline-flex items-center gap-2">
                            <span className="inline-flex items-center justify-center min-w-[26px] h-[18px] px-1.5 rounded-md bg-[#ECEEF3] text-[#525062] font-semibold text-[10px]">
                              {blockNumber}
                            </span>
                            <span className="text-[#828A99]">{timeLabel}</span>
                          </span>
                        </td>
                        <td className="text-right text-[#525062] px-3.5 py-2 border-b border-[#F4F5F8] tabular-nums text-[11px]">
                          {b.scheduled_mwh.toFixed(2)}
                        </td>
                        <td className="text-right text-[#525062] px-3.5 py-2 border-b border-[#F4F5F8] tabular-nums text-[11px]">
                          {b.actual_mwh.toFixed(2)}
                        </td>
                        <td
                          className={`text-right px-3.5 py-2 border-b border-[#F4F5F8] tabular-nums text-[11px] ${
                            b.delta_mwh < 0
                              ? "text-[#E0463C]"
                              : "text-[#1F8A39]"
                          }`}
                        >
                          {b.delta_mwh.toFixed(2)}
                        </td>
                        <td
                          className={`text-right px-3.5 py-2 border-b border-[#F4F5F8] tabular-nums text-[11px] ${
                            b.delta_pct < 0
                              ? "text-[#E0463C]"
                              : "text-[#1F8A39]"
                          }`}
                        >
                          {b.delta_pct.toFixed(1)}%
                        </td>
                      </tr>
                      );
                    })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({
  children,
  align,
}: {
  children: React.ReactNode;
  align: "left" | "right" | "center";
}) {
  return (
    <th
      className={`${
        align === "left" ? "text-left" : "text-right"
      } font-semibold text-[10px] tracking-wider text-[#8C8A96] px-3.5 py-2.5 border-b border-[#ECEEF3]`}
    >
      {children}
    </th>
  );
}

export default PeriodBreakdownTable;
